import axiosInstance from "config/axiosInstance";
import { ClassSortValue } from "config/constants";
import type {
  Class,
  ClassFormData,
  ClassPublishReadiness,
  ClassWithStudentCount,
} from "types/Class";
import type { Student } from "types/Student";
import store from "store";
import { clearClassOptionCache } from "./classOptionCache";

const getClassYears = async (): Promise<number[]> => {
  try {
    const res = await axiosInstance.get("/v1/classes/academic-years/");
    return res.data;
  } catch (e) {
    throw e;
  }
};

const getClasses = async (): Promise<Class[]> => {
  try {
    const params = {
      order_by: ClassSortValue.name_asc,
    };
    const res = await axiosInstance.get("/v1/classes/", { params });
    return res.data;
  } catch (e) {
    throw e;
  }
};

const getClassesFromAcademicYear = async (year: number): Promise<Class[]> => {
  try {
    const { sortBy } = store.getState().class;
    const params = {
      year,
      order_by: sortBy,
    };
    const res = await axiosInstance.get("/v1/classes/", { params });
    return res.data;
  } catch (e) {
    throw e;
  }
};

const getClassesWithStudentCountFromAcademicYear = async (
  year: number,
): Promise<ClassWithStudentCount[]> => {
  try {
    const { sortBy } = store.getState().class;
    const params = {
      year,
      order_by: sortBy,
    };
    const res = await axiosInstance.get("/v1/classes/with-student-count/", {
      params,
    });
    return res.data;
  } catch (e) {
    throw e;
  }
};

const getClassPublishReadinessFromAcademicYear = async (
  year: number,
): Promise<ClassPublishReadiness[]> => {
  try {
    const { sortBy } = store.getState().class;
    const params = {
      year,
      order_by: sortBy,
    };
    const res = await axiosInstance.get("/v1/classes/publish-readiness/", {
      params,
    });
    return res.data;
  } catch (e) {
    throw e;
  }
};

const createClass = async (formData: ClassFormData): Promise<Class> => {
  try {
    const res = await axiosInstance.post("/v1/classes/", formData);
    clearClassOptionCache();
    return res.data;
  } catch (e) {
    throw e;
  }
};

const updateClass = async (
  id: number,
  formData: ClassFormData,
): Promise<Class> => {
  try {
    const res = await axiosInstance.patch(`/v1/classes/${id}/`, formData);
    clearClassOptionCache();
    return res.data;
  } catch (e) {
    throw e;
  }
};

const deleteClass = async (id: number) => {
  try {
    await axiosInstance.delete(`/v1/classes/${id}/`);
    clearClassOptionCache();
  } catch (e) {
    throw e;
  }
};

const getDeletionToken = async (
  password: string,
  operation: string,
): Promise<string> => {
  try {
    const res = await axiosInstance.post("/v1/deletion_token/", {
      password,
      operation,
    });
    return res.data.token;
  } catch (e) {
    throw e;
  }
};

const deleteClassWithToken = async (id: number, token: string) => {
  try {
    await axiosInstance.post(`/v1/classes/${id}/delete_with_token/`, {
      token,
    });
    clearClassOptionCache();
  } catch (e) {
    throw e;
  }
};

const checkClassHasRegisteredParents = async (
  classId: number,
): Promise<boolean> => {
  try {
    const res = await axiosInstance.get("/v1/students/", {
      params: { class_id: classId },
    });
    const students: Student[] = res.data;
    return students.some((student) => student.parent !== null);
  } catch (e) {
    throw e;
  }
};

export {
  getClassYears,
  getClasses,
  getClassesFromAcademicYear,
  getClassesWithStudentCountFromAcademicYear,
  getClassPublishReadinessFromAcademicYear,
  createClass,
  updateClass,
  deleteClass,
  getDeletionToken,
  deleteClassWithToken,
  checkClassHasRegisteredParents,
};
